import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Save, Plus, Trash2, Edit3, X, ChevronDown, ChevronUp,
  Compass, Eye, Image, FileText, Search, ArrowUp, Target
} from "lucide-react"
import {
  heroService, problemService, timelineService, featureService,
  howItWorksService, missionVisionService, valueService,
  serviceService, cityService, teamMemberService
} from "@/services/firebase"
import { CloudinaryImageField } from "@/components/admin/CloudinaryImageField"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ConfirmationModal } from "@/components/shared/ConfirmationModal"
import toast from "react-hot-toast"
import { cn } from "@/lib/utils";

const CONTENT_SECTIONS = [
  { id: "hero", label: "Hero", icon: Image, service: heroService, singular: "hero" },
  { id: "problem", label: "Le Constat", icon: FileText, service: problemService, singular: "section" },
  { id: "timeline", label: "Timeline", icon: FileText, service: timelineService, singular: "événement" },
  { id: "features", label: "Fonctionnalités", icon: FileText, service: featureService, singular: "fonctionnalité" },
  { id: "howItWorks", label: "Comment ça marche", icon: FileText, service: howItWorksService, singular: "section" },
  { id: "missionVision", label: "Mission & Vision", icon: FileText, service: missionVisionService, singular: "section" },
  { id: "values", label: "Valeurs", icon: FileText, service: valueService, singular: "valeur" },
  { id: "services", label: "Services", icon: FileText, service: serviceService, singular: "service" },
  { id: "cities", label: "Communes", icon: FileText, service: cityService, singular: "commune" },
  { id: "team", label: "Équipe", icon: FileText, service: teamMemberService, singular: "membre" }
]

export function ContentAdminPage() {
  const [activeTab, setActiveTab] = useState("hero")
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [editingItem, setEditingItem] = useState<any>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [itemToDelete, setItemToDelete] = useState<{ id: string, singular: string } | null>(null)

  const currentSection = CONTENT_SECTIONS.find(s => s.id === activeTab)

  useEffect(() => {
    loadItems()
  }, [activeTab])

  const loadItems = async () => {
    setLoading(true)
    try {
      const section = CONTENT_SECTIONS.find(s => s.id === activeTab)
      if (section) {
        const data = await section.service.getAll()
        setItems(data)
      }
    } catch (error) {
      toast.error("Erreur lors du chargement")
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (id: string, singular: string) => {
    setItemToDelete({ id, singular })
  }

  const confirmDelete = async () => {
    if (!itemToDelete) return
    const section = CONTENT_SECTIONS.find(s => s.id === activeTab)
    if (!section) return

    try {
      await section.service.delete(itemToDelete.id)
      toast.success(`${itemToDelete.singular.charAt(0).toUpperCase() + itemToDelete.singular.slice(1)} supprimé`)
      loadItems()
    } catch {
      toast.error("Erreur lors de la suppression")
    } finally {
      setItemToDelete(null)
    }
  }


  const getEmptyItem = () => {
    switch (activeTab) {
      case "hero":
        return { title: "", subtitle: "", description: "", ctaClientText: "", ctaArtisanText: "", stats: [] }
      case "timeline":
        return { year: "", month: "", title: "", description: "", icon: "", highlight: false, order: items.length + 1 }
      case "features":
        return { icon: "", title: "", description: "", color: "#E8671A", highlight: false, order: items.length + 1 }
      case "values":
        return { icon: "", title: "", description: "", color: "#E8671A", order: items.length + 1 }
      case "services":
        return { icon: "", name: "", slug: "", description: "", color: "#E8671A", order: items.length + 1 }
      case "missionVision":
      return { 
        missionTitle: "🎯 Notre Mission", 
        missionText: "", 
        missionIcon: "target",
        visionTitle: "🌏 Notre Vision", 
        visionText: "", 
        visionIcon: "globe",
        order: items.length + 1 
      }
      case "cities":
        return { name: "", slug: "", icon: "map-pin", order: items.length + 1 }
      case "team":
        return { name: "", role: "", bio: "", photoURL: "", phone: "", email: "", skills: [], order: items.length + 1 }
      default:
        return { title: "", description: "" }
    }
  }

  const openCreateForm = () => {
    setEditingItem(getEmptyItem())
    setIsEditing(false)
    setIsFormOpen(true)
  }

  const openEditForm = (item: any) => {
    setEditingItem({ ...item })
    setIsEditing(true)
    setIsFormOpen(true)
  }

  const handleSave = async () => {
    if (!editingItem) return

    try {
      const section = CONTENT_SECTIONS.find(s => s.id === activeTab)
      if (!section) return

      if (isEditing && editingItem.id) {
        await section.service.update(editingItem.id, editingItem)
        toast.success(`${section.singular.charAt(0).toUpperCase() + section.singular.slice(1)} mis à jour`)
      } else {
        await section.service.create(editingItem)
        toast.success(`${section.singular.charAt(0).toUpperCase() + section.singular.slice(1)} créé`)
      }

      setIsFormOpen(false)
      setEditingItem(null)
      loadItems()
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde")
      console.error(error)
    }
  }

  // const handleDelete = async (id: string) => {
  //   const section = CONTENT_SECTIONS.find(s => s.id === activeTab)
  //   if (!section) return

  //   if (!confirm(`Êtes-vous sûr de vouloir supprimer ce ${section.singular} ?`)) return

  //   try {
  //     await section.service.delete(id)
  //     toast.success(`${section.singular.charAt(0).toUpperCase() + section.singular.slice(1)} supprimé`)
  //     loadItems()
  //   } catch {
  //     toast.error("Erreur lors de la suppression")
  //   }
  // }

  const handleToggleActive = async (item: any) => {
    const section = CONTENT_SECTIONS.find(s => s.id === activeTab)
    if (!section) return

    try {
      await section.service.toggleActive(item.id, item.active !== false)
      toast.success(item.active !== false ? "Élément désactivé" : "Élément activé")
      loadItems()
    } catch {
      toast.error("Erreur")
    }
  }

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedItems(newExpanded)
  }

  const renderFormFields = () => {
    if (!editingItem) return null

    switch (activeTab) {
      case "hero":
        return (
          <div className="space-y-4">
            <Input label="Titre principal" value={editingItem.title || ""} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
            <Input label="Sous-titre" value={editingItem.subtitle || ""} onChange={(e) => setEditingItem({ ...editingItem, subtitle: e.target.value })} />
            <Textarea label="Description" value={editingItem.description || ""} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={3} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="CTA Client" value={editingItem.ctaClientText || ""} onChange={(e) => setEditingItem({ ...editingItem, ctaClientText: e.target.value })} />
              <Input label="CTA Artisan" value={editingItem.ctaArtisanText || ""} onChange={(e) => setEditingItem({ ...editingItem, ctaArtisanText: e.target.value })} />
            </div>
          </div>
        )

      case "timeline":
        return (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Année" value={editingItem.year || ""} onChange={(e) => setEditingItem({ ...editingItem, year: e.target.value })} />
              <Input label="Mois" value={editingItem.month || ""} onChange={(e) => setEditingItem({ ...editingItem, month: e.target.value })} />
            </div>
            <Input label="Titre" value={editingItem.title || ""} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
            <Textarea label="Description" value={editingItem.description || ""} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={3} />
            <Input label="Icône" value={editingItem.icon || ""} onChange={(e) => setEditingItem({ ...editingItem, icon: e.target.value })} />
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={editingItem.highlight || false} onChange={(e) => setEditingItem({ ...editingItem, highlight: e.target.checked })} />
              <label>Événement marquant</label>
            </div>
            <Input label="Ordre" type="number" value={editingItem.order || 1} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) })} />
          </div>
        )

      case "features":
      case "values":
        return (
          <div className="space-y-4">
            <Input label="Icône (nom Lucide)" value={editingItem.icon || ""} onChange={(e) => setEditingItem({ ...editingItem, icon: e.target.value })} />
            <Input label="Titre" value={editingItem.title || editingItem.name || ""} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value, name: e.target.value })} />
            <Textarea label="Description" value={editingItem.description || ""} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={3} />
            <div>
              <label className="block text-sm font-medium mb-1.5">Couleur</label>
              <div className="flex items-center gap-3">
                <input type="color" value={editingItem.color || "#E8671A"} onChange={(e) => setEditingItem({ ...editingItem, color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={editingItem.color || "#E8671A"} onChange={(e) => setEditingItem({ ...editingItem, color: e.target.value })} />
              </div>
            </div>
            {activeTab === "features" && (
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editingItem.highlight || false} onChange={(e) => setEditingItem({ ...editingItem, highlight: e.target.checked })} />
                <label>Mettre en avant</label>
              </div>
            )}
            <Input label="Ordre" type="number" value={editingItem.order || 1} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) })} />
          </div>
        )

      case "services":
        return (
          <div className="space-y-4">
            <Input label="Nom du service" value={editingItem.name || ""} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} />
            <Input label="Slug (identifiant URL)" value={editingItem.slug || ""} onChange={(e) => setEditingItem({ ...editingItem, slug: e.target.value })} />
            <Input label="Icône" value={editingItem.icon || ""} onChange={(e) => setEditingItem({ ...editingItem, icon: e.target.value })} />
            <Textarea label="Description" value={editingItem.description || ""} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={2} />
            <div>
              <label className="block text-sm font-medium mb-1.5">Couleur</label>
              <div className="flex items-center gap-3">
                <input type="color" value={editingItem.color || "#E8671A"} onChange={(e) => setEditingItem({ ...editingItem, color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={editingItem.color || "#E8671A"} onChange={(e) => setEditingItem({ ...editingItem, color: e.target.value })} />
              </div>
            </div>
            <Input label="Ordre" type="number" value={editingItem.order || 1} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) })} />
          </div>
        )


      case "missionVision":
      return (
        <div className="space-y-4">
          {/* Section Mission */}
          <div className="p-4 border rounded-lg bg-slate-50/50 space-y-4">
            <Badge variant="outline" className="mb-2">Partie Mission</Badge>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input 
                label="Titre Mission" 
                value={editingItem.missionTitle || ""} 
                onChange={(e) => setEditingItem({ ...editingItem, missionTitle: e.target.value })} 
              />
              <Input 
                label="Icône Mission" 
                value={editingItem.missionIcon || ""} 
                onChange={(e) => setEditingItem({ ...editingItem, missionIcon: e.target.value })} 
              />
            </div>
            <Textarea 
              label="Texte Mission" 
              value={editingItem.missionText || ""} 
              onChange={(e) => setEditingItem({ ...editingItem, missionText: e.target.value })} 
              rows={4} 
            />
          </div>

          {/* Section Vision */}
          <div className="p-4 border rounded-lg bg-slate-50/50 space-y-4">
            <Badge variant="outline" className="mb-2">Partie Vision</Badge>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input 
                label="Titre Vision" 
                value={editingItem.visionTitle || ""} 
                onChange={(e) => setEditingItem({ ...editingItem, visionTitle: e.target.value })} 
              />
              <Input 
                label="Icône Vision" 
                value={editingItem.visionIcon || ""} 
                onChange={(e) => setEditingItem({ ...editingItem, visionIcon: e.target.value })} 
              />
            </div>
            <Textarea 
              label="Texte Vision" 
              value={editingItem.visionText || ""} 
              onChange={(e) => setEditingItem({ ...editingItem, visionText: e.target.value })} 
              rows={4} 
            />
          </div>
        </div>
      )

      case "cities":
        return (
          <div className="space-y-4">
            <Input label="Nom de la commune" value={editingItem.name || ""} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} />
            <Input label="Slug" value={editingItem.slug || ""} onChange={(e) => setEditingItem({ ...editingItem, slug: e.target.value })} />
            <Input label="Icône" value={editingItem.icon || "map-pin"} onChange={(e) => setEditingItem({ ...editingItem, icon: e.target.value })} />
            <Input label="Ordre" type="number" value={editingItem.order || 1} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) })} />
          </div>
        )

      case "team":
        return (
          <div className="space-y-4">
            <Input label="Nom complet" value={editingItem.name || ""} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} />
            <Input label="Rôle" value={editingItem.role || ""} onChange={(e) => setEditingItem({ ...editingItem, role: e.target.value })} />
            <CloudinaryImageField
              label="Photo"
              value={editingItem.photoURL || ""}
              folder="team"
              onChange={(photoURL) => setEditingItem({ ...editingItem, photoURL })}
              hint="Portrait affiché dans la section équipe."
            />
            <Textarea label="Biographie" value={editingItem.bio || ""} onChange={(e) => setEditingItem({ ...editingItem, bio: e.target.value })} rows={4} />
            <Input label="Téléphone" value={editingItem.phone || ""} onChange={(e) => setEditingItem({ ...editingItem, phone: e.target.value })} />
            <Input label="Email" type="email" value={editingItem.email || ""} onChange={(e) => setEditingItem({ ...editingItem, email: e.target.value })} />
            <Input label="Compétences (séparées par des virgules)" value={Array.isArray(editingItem.skills) ? editingItem.skills.join(", ") : ""} onChange={(e) => setEditingItem({ ...editingItem, skills: e.target.value.split(",").map((s: string) => s.trim()) })} />
            <Input label="Ordre" type="number" value={editingItem.order || 1} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) })} />
          </div>
        )

      default:
        return (
          <div className="space-y-4">
            <Input label="Titre" value={editingItem.title || ""} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
            <Textarea label="Description" value={editingItem.description || ""} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={4} />
          </div>
        )
    }
  }

  const renderItemCard = (item: any) => {
    const isExpanded = expandedItems.has(item.id)

    switch (activeTab) {
      case "hero":
        return (
          <Card className={item.active === false ? "opacity-50" : ""}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-bold text-dark text-lg">{item.title}</h4>
                  <p className="text-gray-500 text-sm mt-1">{item.subtitle}</p>
                  <p className="text-gray-400 text-xs mt-2 line-clamp-2">{item.description}</p>
                  {item.stats && (
                    <div className="flex gap-4 mt-3">
                      {item.stats.map((stat: any) => (
                        <Badge key={stat.id} variant="secondary" className="text-xs">
                          {stat.value}{stat.suffix} {stat.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <button onClick={() => openEditForm(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleToggleActive(item)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg">
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )

      case "timeline":
        return (
          <Card className={item.active === false ? "opacity-50" : ""}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">{item.year}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-dark">{item.title}</h4>
                    {item.highlight && <Badge variant="default" className="text-xs">🚀 Lancement</Badge>}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">{item.month} — {item.description}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditForm(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id, currentSection?.singular || "élément")} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )

      case "features":
      case "values":
        return (
          <Card className={item.active === false ? "opacity-50" : ""}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-dark">{item.title || item.name}</h4>
                    {item.highlight && <Badge variant="default" className="text-xs">⭐</Badge>}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">{item.description}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditForm(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id, currentSection?.singular || "élément")} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )

      case "services":
        return (
          <Card className={item.active === false ? "opacity-50" : ""}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }} />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-dark">{item.name}</h4>
                  <p className="text-gray-500 text-sm">{item.description}</p>
                  <p className="text-gray-400 text-xs mt-1">Slug: {item.slug}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditForm(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id, currentSection?.singular || "élément")} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      
      case "missionVision":
  return (
    <Card className={cn(
      "overflow-hidden transition-all hover:shadow-md",
      item.active === false ? "opacity-60 grayscale-[0.5]" : ""
    )}>
      <CardContent className="p-0">
        {/* Header avec l'ordre et le statut */}
        <div className="px-4 py-2 bg-slate-50 border-b flex justify-between items-center">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Configuration #{item.order}
          </span>
          {item.active === false && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none text-[10px]">
              Inactif
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {/* Bloc Mission */}
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <Target className="w-4 h-4" />
              <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wide">
                {item.missionTitle || "Notre Mission"}
              </h4>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed italic border-l-2 border-primary/20 pl-3">
              "{item.missionText || "Mission non renseignée..."}"
            </p>
          </div>

          {/* Bloc Vision */}
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-indigo-500">
              <Compass className="w-4 h-4" />
              <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wide">
                {item.visionTitle || "Notre Vision"}
              </h4>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed italic border-l-2 border-indigo-500/20 pl-3">
              "{item.visionText || "Vision non renseignée..."}"
            </p>
          </div>
        </div>

        {/* Barre d'actions épurée */}
        <div className="px-4 py-3 bg-white border-t flex justify-end gap-1">
          <button 
            onClick={() => handleToggleActive(item)} 
            className={cn(
              "p-2 rounded-md transition-colors", 
              item.active === false ? "text-green-600 hover:bg-green-50" : "text-slate-400 hover:bg-slate-100"
            )}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button 
            onClick={() => openEditForm(item)} 
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button 
            onClick={() => handleDelete(item.id, currentSection?.singular || "élément")} 
            className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  )

      case "cities":
        return (
          <Card className={item.active === false ? "opacity-50" : ""}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <span className="text-primary text-xs">📍</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-dark">{item.name}</h4>
                    <p className="text-gray-400 text-xs">Slug: {item.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditForm(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id, currentSection?.singular || "élément")} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )

      case "team":
        return (
          <Card className={item.active === false ? "opacity-50" : ""}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                {item.photoURL ? (
                  <img src={item.photoURL} alt={item.name} className="flex-shrink-0 w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold">
                    {item.name?.split(" ").map((n: string) => n[0]).join("")}
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-bold text-dark">{item.name}</h4>
                  <p className="text-primary text-sm">{item.role}</p>
                  <p className="text-gray-500 text-xs mt-1 line-clamp-2">{item.bio}</p>
                  {item.skills && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {item.skills.map((skill: string, i: number) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">{skill}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditForm(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id, currentSection?.singular || "élément")} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )

      default:
        return (
          <Card className={item.active === false ? "opacity-50" : ""}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-bold text-dark">{item.title}</h4>
                  <p className="text-gray-500 text-sm mt-1">{item.description}</p>
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <button onClick={() => openEditForm(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id, currentSection?.singular || "élément")} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-dark">Gestion du Contenu</h1>
          <p className="text-gray-500 text-sm">Modifier toutes les sections du site</p>
        </div>
        {activeTab !== "hero" && activeTab !== "problem" && activeTab !== "howItWorks" && activeTab !== "missionVision" && (
          <Button onClick={openCreateForm} className="bg-primary hover:bg-primary-dark">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter {currentSection?.singular}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {CONTENT_SECTIONS.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveTab(section.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === section.id
                ? "bg-primary text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <section.icon className="w-4 h-4" />
            {section.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-4">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400">Aucun élément dans cette section</p>
              {activeTab !== "hero" && activeTab !== "problem" && activeTab !== "howItWorks" && activeTab !== "missionVision" && (
                <Button onClick={openCreateForm} className="mt-4 bg-primary hover:bg-primary-dark">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter le premier {currentSection?.singular}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          items.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderItemCard(item)}
            </motion.div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-dark">
                  {isEditing ? `Modifier ${currentSection?.singular}` : `Nouveau ${currentSection?.singular}`}
                </h3>
                <button onClick={() => setIsFormOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {renderFormFields()}

              <div className="flex gap-3 mt-6">
                <Button onClick={handleSave} className="flex-1 bg-primary hover:bg-primary-dark">
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? "Mettre à jour" : "Créer"}
                </Button>
                <Button variant="outline" onClick={() => setIsFormOpen(false)} className="flex-1">
                  Annuler
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <ConfirmationModal
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={confirmDelete}
        title={`Supprimer ${currentSection?.singular || "l'élément"}`}
        message={`Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.`}
      />
    </div>
  )
}
